import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsentBanner from './components/CookieConsentBanner';
import RootRedirect from './components/RootRedirect';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import StoryPage from './pages/StoryPage';
import HealthcarePage from './pages/HealthcarePage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ValtoPage from './pages/ValtoPage';
import NayziPage from './pages/NayziPage';
import ValtocoCasePage from './pages/ValtocoCasePage';
import HydrocortisoneCasePage from './pages/HydrocortisoneCasePage';
import EpipenCasePage from './pages/EpipenCasePage';
import NaloxoneCasePage from './pages/NaloxoneCasePage';
import GuidesPage from './pages/GuidesPage';
import ProjectACTPage from './pages/ProjectACTPage';
import { routes } from './config/routeMap';
import './App.css';

/**
 * Scroll to top / hash on route change. Meta management is now done per-page
 * via <SEOHead> (React 19 native head hoisting), not from here.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

/**
 * Chrome (Header + Footer + Consent) — hidden on the root x-default page
 * (RootLanguageChooser) which has its own minimal chrome.
 */
function AppChrome({ children }) {
  const { pathname } = useLocation();
  const isRoot = pathname === '/';
  if (isRoot) {
    return (
      <>
        {children}
        <CookieConsentBanner />
      </>
    );
  }
  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <div className="flex-1">{children}</div>
      <Footer />
      <CookieConsentBanner />
    </div>
  );
}

/**
 * Build the full <Routes> tree: for each entry in routeMap, we mount the
 * component on BOTH its /en/ path and its /fr/ path. Legacy pre-Phase-C
 * URLs are converted into client-side <Navigate replace> as a safety net —
 * the authoritative 301 redirect is defined server-side (see
 * /app/memory/redirections-nginx.conf).
 */
const componentByKey = {
  home: HomePage,
  products: ProductsPage,
  story: StoryPage,
  healthcare: HealthcarePage,
  contact: ContactPage,
  faq: FAQPage,
  privacy: PrivacyPage,
  terms: TermsPage,
  guides: GuidesPage,
  projectAct: ProjectACTPage,
  valto: ValtoPage,
  nayzi: NayziPage,
  valtocoCase: ValtocoCasePage,
  hydrocortisoneCase: HydrocortisoneCasePage,
  epipenCase: EpipenCasePage,
  naloxoneCase: NaloxoneCasePage,
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <ScrollToTop />
        <AppChrome>
          <Routes>
            {/* Root — auto-redirect to /fr or /en based on stored
                preference, browser language, or EN fallback */}
            <Route path="/" element={<RootRedirect />} />

            {/* Localized routes — one component mounted on two paths */}
            {Object.entries(routes).map(([key, entry]) => {
              const Comp = componentByKey[key];
              if (!Comp) return null;
              return (
                <React.Fragment key={key}>
                  <Route path={entry.en} element={<Comp />} />
                  <Route path={entry.fr} element={<Comp />} />
                </React.Fragment>
              );
            })}

            {/* Client-side safety-net redirects for the pre-Phase-C URLs.
                Real 301s must be applied at the reverse-proxy layer
                (Nginx / Cloudflare) — see redirections-nginx.conf. */}
            {Object.entries(routes).map(([key, entry]) => {
              if (!entry.legacy || entry.legacy === '/') return null;
              return (
                <Route
                  key={`legacy-${key}`}
                  path={entry.legacy}
                  element={<Navigate to={entry.en} replace />}
                />
              );
            })}
          </Routes>
        </AppChrome>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
